import { Alert, Button, Checkbox, Col, Form, Input, Modal, Row, Space } from 'antd';
import { AppCopyright, AppLogo } from 'components';
import { useLogin } from 'hooks/useLogin';
import { LocaleChanger } from 'pages/components';
import { DownloadTable } from 'pages/help/Download';
import React, { useState } from 'react';
import styled from 'styled-components';
import ResetPassword from '../../../pages/auth/ResetPassword';
import { kpassLogin } from '../service';

const styles = {
  formHeight: {
    height: 40,
  },
};

const StyledBackground = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  .loginBox {
    margin: 0 auto;
    padding: 40px;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 1px 1px 1px rgba(0, 0, 0, 0.07), 0 3px 4px -1px rgba(0, 0, 0, 0.07),
      0 4px 9px 2px rgba(0, 0, 0, 0.07);
  }
`;

const StyledBox = styled.div`
  margin: 0 auto;
  width: 368px;
  padding: 40px;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 1px 1px 1px rgba(0, 0, 0, 0.07), 0 3px 4px -1px rgba(0, 0, 0, 0.07),
    0 4px 9px 2px rgba(0, 0, 0, 0.07);
`;

function Login(props) {
  const { history, location, backConfig } = props;

  // states
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [serviceGuideLink, setServiceGuideLink] = useState(null);
  const [checked, setChecked] = useState(false);
  const [resetPwdProps, setResetPwdProps] = useState({ visible: false }); // 비밀번호 변경 컴포넌트 props
  const [loginLoading, setLoginLoading] = useState(false); // 로그인 조회 로딩

  const { login, ui, settings } = useLogin({
    setResetPwdProps,
    setServiceGuideLink,
    checked,
    history,
    location,
  });

  const { loginForm, customUrls, credential, loginDispatch } = login;
  const { hideMenus, theme } = ui;
  const { config, locale, error, setError } = settings;

  const { MultiFactor } = config;

  /**
   * ! 로그인 API가 다르기 때문에 customResult를 사용 못 함
   * ! ldap 773, 532 대응 안하기로 협의 됨
   * ! 환경설정의 config.authentication.two-factor-auth.use와는 무관하게 무조건 SMS로 OTP 실행한다.
   * @param {object} values 폼 값
   */
  const onFinish = async (values) => {
    setError(null);
    setLoginLoading(true);
    try {
      if (backConfig && !backConfig.externalServerConnection.kpass.apiUrl) {
        throw new Error('txt.kpass.require-setting');
      }
      const payload = new URLSearchParams(values).toString();

      const res = await kpassLogin(backConfig.externalServerConnection.kpass.apiUrl, payload);
      // 성공 조건은 result가 `evalSuccess` 혹은 `success` 인 경우
      if (res.data.result.includes('uccess')) {
        loginDispatch({
          type: 'set-credential',
          payload: {
            ...credential,
            ...values,
            // idpw는 useLogin에서 로그인할 때 localStorage에 세팅하는 정보이기 때문에 필요함
            idpw: {
              userId: values.name, // 상단 앱바에 사용자 이름을 표시하기 위함
              username: values.name,
              autoConnect: false,
              redirectComplete: false,
              ...(config.autoLogin && { autoLogin: checked }),
            },
            twoFactor: { type: 'require' },
          },
        });
      } else if (res.data.result === 'fail') {
        throw new Error('txt.kpass.error.fail');
      } else if (res.data.result === 'noEval') {
        throw new Error('txt.kpass.error.noEval');
      } else if (res.data.result === 'differLoginId') {
        throw new Error('txt.kpass.error.differLoginId');
      } else if (res.data.result === 'noIp') {
        throw new Error('txt.kpass.error.noIp');
      } else {
        throw new Error('txt.kpass.error.unknown');
      }
    } catch (err) {
      setError(locale.resource[err.message] || err.message);
    }
    setLoginLoading(false);
  };

  return (
    <StyledBackground>
      <div style={{ position: 'absolute', top: '1em', right: '1em' }}>
        <LocaleChanger />
      </div>
      <StyledBox style={config.loginBoxStyles}>
        <Row gutter={[0, 32]}>
          <Col span={24} style={{ textAlign: 'center' }}>
            <AppLogo />
          </Col>
          <Col span={24}>
            {/* startsWith('require') = require | require-secret-key */}
            {Object.prototype.hasOwnProperty.call(credential, 'twoFactor') &&
            credential.twoFactor.type.startsWith('require') ? (
              <MultiFactor credential={credential} loginDispatch={loginDispatch} />
            ) : (
              <>
                {error && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 24 }}
                    message={<div style={{ whiteSpace: 'pre-line' }}>{error}</div>}
                  />
                )}
                <Form
                  onFinish={onFinish}
                  layout="horizontal"
                  form={loginForm}
                  labelCol={{ flex: '58px' }}
                  wrapperCol={{ flex: 'auto' }}
                  requiredMark={false}
                  size="large"
                >
                  <Form.Item
                    label={locale.resource['txt.kpass.name']}
                    name="name"
                    style={{ flexWrap: 'nowrap' }}
                    rules={[{ required: true, message: locale.resource['txt.required-field'] }]}
                  >
                    <Input autoFocus />
                  </Form.Item>
                  <Form.Item
                    label={locale.resource['txt.kpass.birth']}
                    name="birth"
                    style={{ flexWrap: 'nowrap' }}
                    rules={[
                      { required: true, message: locale.resource['txt.required-field'] },
                      { len: 8 },
                      {
                        validator: (rule, value) => {
                          const reg = new RegExp('^[0-9]*$');
                          return reg.test(value)
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(locale.resource['validation.vdgroup.vd-count-parsing']),
                              );
                        },
                      },
                    ]}
                  >
                    <Input placeholder="YYYYMMDD" />
                  </Form.Item>
                  <Form.Item
                    label={locale.resource['txt.kpass.email']}
                    name="email"
                    style={{ flexWrap: 'nowrap' }}
                    rules={[
                      { required: true, message: locale.resource['txt.required-field'] },
                      { type: 'email' },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <div style={{ marginBlock: 16 }}>
                    {
                      // 자동로그인 옵션을 사용하고, 생체인증 사용자가 로그아웃한 경우가 아닌 경우에 자동로그인 체크박스 출력
                      config.autoLogin &&
                        !(
                          sessionStorage.getItem('logout') === 'true' &&
                          localStorage.getItem('bioAuth') === 'true'
                        ) && (
                          <Form.Item name="autoLogin" noStyle>
                            <Checkbox
                              checked={checked}
                              onChange={(e) => setChecked(e.target.checked)}
                            >
                              {locale.resource['txt.auto-login']}
                            </Checkbox>
                          </Form.Item>
                        )
                    }
                    {config.saveId && (
                      <Form.Item name="saveId" noStyle valuePropName="checked" initialValue={false}>
                        <Checkbox>{locale.resource['txt.save-id']}</Checkbox>
                      </Form.Item>
                    )}
                  </div>
                  <Form.Item style={{ margin: '24px 0 0' }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      style={styles.formHeight}
                      loading={loginLoading}
                    >
                      LOGIN
                    </Button>
                  </Form.Item>
                </Form>
              </>
            )}
          </Col>
          <Col span={24} style={{ textAlign: 'center' }}>
            <Row gutter={[0, 8]}>
              <Col span={24} style={{ textAlign: 'center' }}>
                <Space>
                  {!hideMenus.includes('signup') && customUrls?.signUp && (
                    <Button
                      type="link"
                      onClick={() => {
                        window.open(
                          customUrls?.signUp,
                          '',
                          `${
                            theme.customUrls?.signUp?.windowFeatures !== undefined
                              ? theme.customUrls.signUp.windowFeatures
                              : `height=${window.screen.height},width=${window.screen.width},resizable=yes,fullscreen=yes`
                          }`,
                        );
                      }}
                    >
                      {locale.resource['txt.register-id']}
                    </Button>
                  )}
                  <div>
                    <ResetPassword
                      resetPwdProps={resetPwdProps}
                      setResetPwdProps={setResetPwdProps}
                    />
                  </div>
                </Space>
              </Col>
              <Col span={24} style={{ textAlign: 'center' }}>
                <Space>
                  {!hideMenus.includes('guide') && (
                    <a href={serviceGuideLink} download className="ant-btn">
                      {locale.resource[theme.serviceGuide.text]}
                    </a>
                  )}
                  {!hideMenus.includes('fileDownload') && (
                    <>
                      <Button onClick={() => setDownloadModalVisible(true)}>
                        {locale.resource['txt.download-client']}
                      </Button>
                      <Modal
                        title={locale.resource['txt.download-client']}
                        centered
                        width={720}
                        visible={downloadModalVisible}
                        onOk={() => setDownloadModalVisible(false)}
                        onCancel={() => setDownloadModalVisible(false)}
                        destroyOnClose
                        footer={[
                          <Button key="ok" onClick={() => setDownloadModalVisible(false)}>
                            {locale.resource['txt.ok']}
                          </Button>,
                        ]}
                      >
                        <DownloadTable size="small" locale={locale} />
                      </Modal>
                    </>
                  )}
                </Space>
              </Col>
            </Row>
          </Col>
        </Row>
      </StyledBox>
      <div style={{ marginTop: 16 }}>
        <AppCopyright />
      </div>
    </StyledBackground>
  );
}

export default Login;
